import { useEffect, useState } from "react";
import { StockRow } from "../StockRow";
import { fetchStock, restockAll, type StockItem } from "../stock";
import { withSpan } from "../otel";
import { signIn } from "../session";

type StockState = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; items: StockItem[] };

type RestockAllPhase = { name: "idle" } | { name: "confirming" } | { name: "restocking" } | { name: "error"; message: string };

/**
 * Stock (#259) — list + badges via `GET /api/admin/stock`, plus the admin
 * write: a Restock button per row (`StockRow.tsx`) plus "restock all", the
 * first bulk/confirm-before-mutate control in any of this repo's UIs — an
 * arm-then-confirm button, not a native `window.confirm()` or a new modal
 * subsystem for one button. The read went through load-generator (ADR
 * 0046) until #262 (ADR 0064) moved it onto inventory-service's own admin
 * Lambda alongside the write.
 */
export function StockScreen() {
  const [state, setState] = useState<StockState>({ status: "loading" });
  const [allPhase, setAllPhase] = useState<RestockAllPhase>({ name: "idle" });

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setState({ status: "loading" });
    const result = await fetchStock();
    if (result.kind === "ok") {
      setState({ status: "ready", items: result.value });
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setState({ status: "error", message: result.message });
    }
  }

  function updateOne(sku: string, quantityOnHand: number) {
    setState((s) =>
      s.status === "ready" ? { ...s, items: s.items.map((i) => (i.sku === sku ? { ...i, quantityOnHand } : i)) } : s,
    );
  }

  async function confirmRestockAll() {
    setAllPhase({ name: "restocking" });
    const result = await withSpan("stock.restock_all", {}, () => restockAll());

    if (result.kind === "ok") {
      setAllPhase({ name: "idle" });
      await load(); // many rows changed — simplest to just refetch
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setAllPhase({ name: "error", message: result.message });
    }
  }

  if (state.status === "loading") {
    return <p className="status-message">Loading stock…</p>;
  }

  if (state.status === "error") {
    return (
      <section className="screen-placeholder">
        <p className="status-error">Couldn't load stock ({state.message}).</p>
        <button type="button" onClick={() => void load()}>
          Retry
        </button>
      </section>
    );
  }

  const outOfStock = state.items.filter((i) => i.quantityOnHand <= 0).length;
  const low = state.items.filter((i) => i.quantityOnHand > 0 && i.quantityOnHand < (i.initialStock ? i.initialStock * 0.1 : 20)).length;

  return (
    <div>
      <div className="stock-summary">
        <p className="stock-summary-text">
          {state.items.length} SKUs
          {outOfStock > 0 || low > 0 ? (
            <>
              {outOfStock > 0 && ` · ${outOfStock} out of stock`}
              {low > 0 && ` · ${low} low`}
            </>
          ) : (
            " · all healthy 🎉"
          )}
        </p>

        <div className="restock-all">
          {allPhase.name === "confirming" ? (
            <>
              <span>Restock all {state.items.length} SKUs?</span>
              <button type="button" className="restock-all-confirm" onClick={confirmRestockAll}>
                Yes, restock all
              </button>
              <button type="button" onClick={() => setAllPhase({ name: "idle" })}>
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setAllPhase({ name: "confirming" })}
              disabled={allPhase.name === "restocking"}
            >
              {allPhase.name === "restocking" ? "Restocking all…" : "Restock all"}
            </button>
          )}
        </div>
      </div>

      {allPhase.name === "error" && <p className="field-error">Couldn't restock all: {allPhase.message}</p>}

      <div className="stock-table-wrap">
        <table className="stock-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Name</th>
              <th className="stock-num">On hand</th>
              <th>Of initial</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {state.items.map((item) => (
              <StockRow key={item.sku} item={item} onRestocked={updateOne} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
